import React, { useRef, useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  Typography,
  FormHelperText,
  CircularProgress,
  type FilterOptionsState,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SearchIcon from "@mui/icons-material/Search";
import HPTag from "./Tag";

interface DropdownOption {
  value: string;
  label: string;
  color?: string;
  subtitle?: string;
}

interface SingleSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  className?: string;
  disabled?: boolean;
  multiSelect?: false;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  countLabel?: never;
  /** Force the search input on/off; defaults to auto (shown when >10 options). */
  searchable?: boolean;
  getOptionDisabled?: (option: DropdownOption) => boolean;
  noOptionsText?: string;
  // Infinite scroll props
  observerTarget?: React.RefObject<HTMLDivElement | null>;
  hasMore?: boolean;
  loading?: boolean;
  onInputChange?: (event: any, value: string) => void;
}

interface MultiSelectProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: DropdownOption[];
  className?: string;
  disabled?: boolean;
  multiSelect: true;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  /** When set, shows "{N} {countLabel}" instead of tag chips when more than one item is selected */
  countLabel?: string;
  /** Force the search input on/off; defaults to auto (shown when >10 options). */
  searchable?: boolean;
  getOptionDisabled?: (option: DropdownOption) => boolean;
  noOptionsText?: string;
  // Infinite scroll props
  observerTarget?: React.RefObject<HTMLDivElement | null>;
  hasMore?: boolean;
  loading?: boolean;
  onInputChange?: (event: any, value: string) => void;
}

type DropdownProps = SingleSelectProps | MultiSelectProps;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const HPDropdown: React.FC<DropdownProps> = ({
  label,
  value,
  onChange,
  options,
  className,
  disabled = false,
  multiSelect = false,
  placeholder = "",
  error,
  helperText,
  countLabel,
  searchable,
  getOptionDisabled,
  noOptionsText,
  observerTarget,
  hasMore,
  loading,
  onInputChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [maxTags, setMaxTags] = useState(0);

  // Only show a searchable/typeable input once there are enough options to
  // warrant filtering; server-driven search (onInputChange) is always
  // searchable regardless of the current page's option count. An explicit
  // `searchable` prop overrides the auto threshold.
  const isSearchable =
    searchable ?? (!!onInputChange || options.length > 10);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, []);

  const measureTextWidth = (text: string, font = "14px Arial"): number => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context) {
      context.font = font;
      const metrics = context.measureText(text);
      return metrics.width + 30;
    }
    return 150;
  };

  useEffect(() => {
    if (!multiSelect || !Array.isArray(value) || !containerRef.current) return;

    const selectedOptions = options.filter((opt) =>
      (value as string[]).includes(opt.value),
    );
    let totalWidth = 0;
    let tagCount = 0;
    const overflowIndicatorWidth = 40;
    const tagGap = 4;
    const padding = 14;
    const inputWidth = containerRef.current.offsetWidth - padding - 40;

    if (selectedOptions.length === 0) {
      setMaxTags(0);
      return;
    }

    for (const option of selectedOptions) {
      const tagWidth = measureTextWidth(option.label);
      if (
        totalWidth + tagWidth + tagGap <=
        inputWidth - overflowIndicatorWidth
      ) {
        totalWidth += tagWidth + tagGap;
        tagCount++;
      } else {
        break;
      }
    }
    setMaxTags(Math.min(tagCount, 4));
  }, [value, options, containerWidth, multiSelect]);

  const handleChange = (
    _: any,
    newValue: DropdownOption | DropdownOption[] | null,
  ) => {
    if (multiSelect) {
      const hasAllOption = options.some((opt) => opt.value === "all");
      const selectedOptions = (newValue as DropdownOption[]) || [];
      let selectedValues = selectedOptions.map((opt) => opt.value);
      if (hasAllOption) {
        if (!Array.isArray(options)) {
          return;
        }
        const allOptionValues = options.map((opt) => opt.value);
        const nonAllOptionValues = allOptionValues.filter((v) => v !== "all");
        const justSelectedAll =
          selectedValues.includes("all") &&
          !(value as string[]).includes("all");
        const justDeselectedAll =
          !selectedValues.includes("all") &&
          (value as string[]).includes("all");
        const allOthersSelected =
          nonAllOptionValues.length > 0 &&
          selectedValues.length === nonAllOptionValues.length &&
          !selectedValues.includes("all");
        if (justSelectedAll || allOthersSelected) {
          selectedValues = allOptionValues;
        } else if (justDeselectedAll) {
          selectedValues = [];
        } else if (
          (value as string[]).includes("all") &&
          selectedValues.length < allOptionValues.length
        ) {
          selectedValues = selectedValues.filter((v) => v !== "all");
        }
      }
      (onChange as (value: string[]) => void)(selectedValues);
    } else {
      const selectedOption = newValue as DropdownOption | null;
      (onChange as (value: string) => void)(
        selectedOption ? selectedOption.value : "",
      );
    }
  };

  const getSelectedOptions = () => {
    if (multiSelect) {
      const selectedValues = value as string[];
      if (
        selectedValues.includes("all") &&
        options.some((opt) => opt.value === "all")
      ) {
        return options;
      }
      if (!Array.isArray(options)) {
        return [];
      }
      return options.filter((opt) => selectedValues.includes(opt.value));
    } else {
      const selectedValue = value as string;
      return options.find((opt) => opt.value === selectedValue) || null;
    }
  };

  const renderOption = (
    props: any,
    option: DropdownOption,
    { selected }: any,
  ) => {
    const { id, key: _, ...otherProps } = props;
    const isDisabled = getOptionDisabled ? getOptionDisabled(option) : false;
    return (
      <li key={id} {...otherProps}>
        {multiSelect && (
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
            disabled={isDisabled}
          />
        )}
        {option.subtitle ? (
          <Box sx={{ opacity: isDisabled ? 0.45 : 1 }}>
            <Typography variant="body2">{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.subtitle}
            </Typography>
          </Box>
        ) : (
          <span style={{ opacity: isDisabled ? 0.45 : 1 }}>{option.label}</span>
        )}
      </li>
    );
  };

  const renderValue = (selectedOptions: DropdownOption[]) => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }
    if (selectedOptions.some((opt) => opt.value === "all")) {
      return "All";
    }

    if (countLabel && selectedOptions.length > 1) {
      return (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", color: "inherit", whiteSpace: "nowrap" }}
        >
          {selectedOptions.length} {countLabel} selected
        </Typography>
      );
    }

    const displayedTags = selectedOptions.slice(0, maxTags);
    const overflowCount = selectedOptions.length - maxTags;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexWrap: "nowrap",
          overflowX: "hidden",
        }}
      >
        {displayedTags.map((option: DropdownOption) => (
          <HPTag
            key={option.value}
            ariaLabel={`Remove ${option.label}`}
            color={option.color || "blue"}
            onClick={() => {
              const newValues = (value as string[]).filter(
                (v) => v !== option.value,
              );
              (onChange as (val: string[]) => void)(newValues);
            }}
            classNames="text-nowrap  text-ellipsis"
          >
            {option.label}
          </HPTag>
        ))}
        {overflowCount > 0 && (
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              minWidth: "24px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            +{overflowCount}
          </Typography>
        )}
      </Box>
    );
  };

  // Create custom ListboxComponent with observer target for infinite scroll
  const ListboxComponentMemo = React.useMemo(() => {
    if (!observerTarget) return undefined;

    return React.forwardRef<
      HTMLUListElement,
      React.HTMLAttributes<HTMLElement>
    >(function ListboxComponent(props, ref) {
      const observerRef = React.useCallback(
        (node: HTMLLIElement | null) => {
          if (node && observerTarget) {
            (observerTarget as React.MutableRefObject<any>).current = node;
          }
        },
        [observerTarget],
      );

      return (
        <ul {...props} ref={ref}>
          {props.children}
          {hasMore && (
            <li
              ref={observerRef}
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "8px",
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={20} />
            </li>
          )}
        </ul>
      );
    });
  }, [observerTarget, hasMore]);

  return (
    <div className={className} ref={containerRef}>
      <label
        className="block text-xs font-medium mb-1"
        style={{
          color: error
            ? "var(--color-hp-error)"
            : "var(--color-hp-text-day-primary)",
        }}
      >
        {label}
      </label>
      <Autocomplete
        multiple={multiSelect}
        options={options}
        disableCloseOnSelect={multiSelect}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={getSelectedOptions()}
        onChange={handleChange}
        disabled={disabled}
        getOptionDisabled={getOptionDisabled}
        renderOption={renderOption}
        renderValue={multiSelect ? (renderValue as any) : undefined}
        ListboxComponent={ListboxComponentMemo}
        filterOptions={
          onInputChange
            ? (x) => x // don't filter for infinite scroll
            : getOptionDisabled
              ? (
                  opts: DropdownOption[],
                  state: FilterOptionsState<DropdownOption>,
                ) => {
                  if (!state.inputValue) return opts;
                  const lower = state.inputValue.toLowerCase();
                  return opts.filter(
                    (opt) =>
                      getOptionDisabled(opt) ||
                      opt.label.toLowerCase().includes(lower),
                  );
                }
              : undefined // MUI default filtering
        }
        noOptionsText={noOptionsText}
        onInputChange={onInputChange}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            error={error}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: error
                  ? "var(--color-hp-error)"
                  : "var(--color-hp-border-default)",
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "14px",
                color: error ? "var(--color-hp-error)" : "#A6A6A6",
              },
            }}
            className="[&>.MuiInputBase-root]:pl-3.5 [&>.MuiInputBase-root]:h-10 [&>.MuiInputBase-root]:rounded-lg"
            placeholder={(value as string[]).length === 0 ? placeholder : ""}
            InputProps={{
              ...params.InputProps,
              readOnly: !isSearchable,
              startAdornment: isSearchable ? (
                <>
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "#A6A6A6", ml: 0.5, mr: 0.5 }}
                  />
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment: (
                <>
                  {loading && options.length === 0 ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {helperText && (
        <FormHelperText
          error={error}
          sx={{
            marginLeft: "14px",
            marginTop: "4px",
            fontSize: "12px",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default HPDropdown;
